import { type Routes } from '@angular/router';

import { loginGuard } from '@core/auth/guards/login.guard';
import { onboardingGuard } from '@core/auth/guards/onboarding.guard';
import { registrationGuard } from '@core/auth/guards/registration.guard';
import { routeGuard } from '@core/guards/route.guard';

import { appointmentRoutes } from '@features/appointment/appointment.routes';
import { AppointmentService } from '@features/appointment/services/appointment.service';
import { CareRecommendationService } from '@features/get-care/services/care-recommendation.service';
import type { LegacyRedirectConfig } from '@features/legacy-redirect/models/legacy-redirect.types';

import { BottomBar } from '@shared/layouts/bottom-bar/bottom-bar';

import { environment } from '../environments/environment';

const loadLegacyRedirect = () =>
  import('@features/legacy-redirect/pages/legacy-redirect/legacy-redirect.page').then((m) => m.LegacyRedirectPage);

export const routes: Routes = [
  // DEV ONLY: Workflow tester for testing workflow configurations from local JSON
  {
    path: 'dev/workflow/:key',
    loadComponent: () =>
      import('@features/workflow-tester/pages/workflow-tester/workflow-tester.page').then((m) => m.WorkflowTesterPage),
  },
  // {
  // TODO: implement this base page & feature
  // path: ':orgSlug/affiliate',
  // loadComponent: () => import('@features/home/pages/home/home.page').then((m) => m.HomePage),
  // canActivate: [routeGuard, registrationGuard],
  // children: [
  //   {
  //     path: 'home',
  //     loadComponent: () => import('@features/home/pages/home/home.page').then((m) => m.HomePage),
  //     canActivate: [routeGuard, registrationGuard],
  //     data: { background: true },
  //   },
  //   {
  //     path: '',
  //     redirectTo: 'login',
  //     pathMatch: 'full',
  //   },
  // ],
  // },
  {
    path: `workflow/${environment.workflowKeys.onboarding}`,
    loadComponent: () => import('@features/workflow/pages/workflow/workflow.page').then((m) => m.WorkflowPage),
    canActivate: [routeGuard, onboardingGuard],
    data: {
      key: environment.workflowKeys.onboarding,
    },
  },
  {
    path: 'workflow/:key',
    loadComponent: () => import('@features/workflow/pages/workflow/workflow.page').then((m) => m.WorkflowPage),
    canActivate: [routeGuard, registrationGuard],
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () => import('@features/login/pages/login/login.page').then((m) => m.LoginPage),
    canActivate: [loginGuard],
    data: { background: true },
  },
  {
    path: 'verify',
    loadComponent: () => import('@features/login/pages/verify/verify.page').then((m) => m.VerifyPage),
  },
  {
    path: 'appointment/:appointmentId',
    loadComponent: () => import('@features/appointment/appointment.page').then((m) => m.AppointmentPage),
    canActivate: [routeGuard, registrationGuard],
    providers: [AppointmentService],
    children: appointmentRoutes,
  },
  {
    path: 'program',
    loadChildren: () => import('@features/program/program.routes').then((m) => m.programRoutes),
    canActivate: [routeGuard, registrationGuard],
  },
  // Legacy URL redirects (temporary - support old app URLs from emails/SMS)
  {
    path: 'subscription/:id/failed-payment',
    redirectTo: ({ params }) => `/account/subscription/${params['id']}`,
  },
  {
    path: 'account/subscriptions/:id',
    redirectTo: ({ params }) => `/account/subscription/${params['id']}`,
  },
  {
    path: 'account/subscriptions/:id/schedule-consultation',
    redirectTo: ({ params }) => `/account/subscription/${params['id']}`,
  },
  {
    path: 'account/subscriptions/:id/shipping-address',
    redirectTo: ({ params }) => `/account/subscription/${params['id']}`,
  },
  {
    path: 'waiting-room/:hash',
    loadComponent: loadLegacyRedirect,
    canActivate: [routeGuard, registrationGuard],
    data: { strategy: 'waiting-room-hash' } satisfies LegacyRedirectConfig,
  },
  {
    path: 'monthly-checkin/:flowId',
    loadComponent: loadLegacyRedirect,
    canActivate: [routeGuard, registrationGuard],
    data: { strategy: 'check-in-activation' } satisfies LegacyRedirectConfig,
  },
  {
    path: 'account/:accountId/subscriptions/:subscriptionId',
    loadComponent: loadLegacyRedirect,
    canActivate: [routeGuard, registrationGuard],
    data: {
      strategy: 'account-switch',
      targetPath: '/account/subscription/:subscriptionId',
    } satisfies LegacyRedirectConfig,
  },
  {
    path: 'account/:accountId/subscriptions/:subscriptionId/schedule-consultation',
    loadComponent: loadLegacyRedirect,
    canActivate: [routeGuard, registrationGuard],
    data: {
      strategy: 'account-switch',
      targetPath: '/account/subscription/:subscriptionId',
    } satisfies LegacyRedirectConfig,
  },
  {
    path: 'account/:accountId/subscriptions/:subscriptionId/shipping-address',
    loadComponent: loadLegacyRedirect,
    canActivate: [routeGuard, registrationGuard],
    data: {
      strategy: 'account-switch',
      targetPath: '/account/subscription/:subscriptionId',
    } satisfies LegacyRedirectConfig,
  },
  {
    path: 'post-visit-summary/:flowId',
    loadComponent: loadLegacyRedirect,
    canActivate: [routeGuard, registrationGuard],
    data: { strategy: 'offer-activation' } satisfies LegacyRedirectConfig,
  },
  {
    path: 'weight-loss/get-started',
    redirectTo: '/program/weight-loss',
  },
  // End of legacy
  {
    path: '',
    component: BottomBar,
    children: [
      {
        path: 'home',
        loadComponent: () => import('@features/home/pages/home/home.page').then((m) => m.HomePage),
        canActivate: [routeGuard, registrationGuard],
        data: { background: true, showTabs: true },
      },
      {
        path: 'primary-care',
        loadChildren: () => import('@features/primary-care/primary-care.routes').then((m) => m.primaryCareRoutes),
        canActivate: [routeGuard, registrationGuard],
      },
      // TODO: Ask BE for a feature flag for showing this feature
      {
        path: 'get-care',
        loadChildren: () => import('@features/get-care/get-care.routes').then((m) => m.getCareRoutes),
        canActivate: [routeGuard, registrationGuard],
        providers: [CareRecommendationService],
      },
      // TODO: Ask BE for a feature flag for showing this feature
      {
        path: 'locations',
        loadChildren: () => import('@features/locations/locations.routes').then((m) => m.locationsRoutes),
        canActivate: [routeGuard, registrationGuard],
        providers: [CareRecommendationService],
      },
      {
        path: 'account',
        loadChildren: () => import('@features/account/account.routes').then((m) => m.accountRoutes),
        canActivate: [routeGuard, registrationGuard],
      },
    ],
  },
  {
    path: 'invite/accept/:id',
    loadComponent: () =>
      import('@features/account/pages/access-request/access-request.page').then((m) => m.AccessRequestPage),
    canActivate: [routeGuard, registrationGuard],
  },
  {
    path: 'failed-payment/:id',
    loadComponent: () =>
      import('@features/payment-failure/pages/failed-payment/failed-payment.page').then((m) => m.FailedPaymentPage),
    canActivate: [routeGuard, registrationGuard],
  },
  {
    path: ':hash',
    loadComponent: () =>
      import('@features/login/pages/link-shortening/link-shortening.page').then((m) => m.LinkShorteningPage),
  },
];
